import { useState } from 'react';
import { useBoletos } from '@/features/financeiro/hooks/useBoletos';
import type { OrdenarPorBoletos } from '@/features/financeiro/services/boletosService';
import { PeriodoFiltro, usePeriodo } from '@/components/listas/PeriodoFiltro';
import { CabecalhoOrdenavel, useOrdenacao } from '@/components/listas/CabecalhoOrdenavel';
import type { BoletoResumo, EmitirBoletoRequest, BaixarBoletoRequest } from '@/features/financeiro/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Download, XCircle, Printer, ChevronLeft, ChevronRight, Search } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toast } from 'sonner';

const PAGE_SIZES = [10, 20, 50] as const;

function statusBoleto(b: BoletoResumo) {
  if (b.isEstornado)         return { label: 'Estornado',  variant: 'outline'       as const };
  if (b.dtCancelamento)      return { label: 'Cancelado',  variant: 'destructive'   as const };
  if (b.isPago)              return { label: 'Pago',       variant: 'default'        as const };
  const venc = new Date(b.dtVencimento);
  if (venc < new Date())     return { label: 'Vencido',    variant: 'destructive'   as const };
  return                            { label: 'A Vencer',   variant: 'secondary'     as const };
}

function fmt(v: number) {
  return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function fmtDate(s: string | null | undefined) {
  if (!s) return '—';
  return format(new Date(s), 'dd/MM/yyyy', { locale: ptBR });
}

// ── Dialogs ───────────────────────────────────────────────────────────────

interface EmitirDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (req: EmitirBoletoRequest) => Promise<void>;
  isLoading: boolean;
}

function EmitirDialog({ open, onClose, onConfirm, isLoading }: EmitirDialogProps) {
  const [clienteId,    setClienteId]    = useState('');
  const [bancoId,      setBancoId]      = useState('');
  const [dtVencimento, setDtVencimento] = useState('');
  const [valorTotal,   setValorTotal]   = useState('');
  const [nossoNumero,  setNossoNumero]  = useState('');
  const [linhaDigit,   setLinhaDigit]   = useState('');
  const [codigoBarras, setCodigoBarras] = useState('');
  const [taxaBoleto,   setTaxaBoleto]   = useState('0');
  const [observacao,   setObservacao]   = useState('');

  const reset = () => {
    setClienteId(''); setBancoId(''); setDtVencimento(''); setValorTotal('');
    setNossoNumero(''); setLinhaDigit(''); setCodigoBarras(''); setTaxaBoleto('0'); setObservacao('');
  };

  const handleSubmit = async () => {
    if (!clienteId || !bancoId || !dtVencimento || !valorTotal) {
      toast.error('Preencha os campos obrigatórios.');
      return;
    }
    await onConfirm({
      clienteId:     parseInt(clienteId),
      bancoId:       parseInt(bancoId),
      dtVencimento,
      valorTotal:    parseFloat(valorTotal),
      nossoNumero:   nossoNumero || undefined,
      linhaDigitavel: linhaDigit || undefined,
      codigoBarras:  codigoBarras || undefined,
      taxaBoleto:    parseFloat(taxaBoleto) || 0,
      observacao:    observacao || undefined,
    });
    reset();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) { reset(); onClose(); } }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Emitir Boleto</DialogTitle>
        </DialogHeader>

        <div className="grid gap-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Cliente ID *</Label>
              <Input type="number" value={clienteId} onChange={e => setClienteId(e.target.value)} placeholder="Ex: 1" />
            </div>
            <div className="space-y-1">
              <Label>Banco ID *</Label>
              <Input type="number" value={bancoId} onChange={e => setBancoId(e.target.value)} placeholder="Ex: 1" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Vencimento *</Label>
              <Input type="date" value={dtVencimento} onChange={e => setDtVencimento(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label>Valor Total *</Label>
              <Input type="number" step="0.01" value={valorTotal} onChange={e => setValorTotal(e.target.value)} placeholder="0,00" />
            </div>
          </div>

          <div className="space-y-1">
            <Label>Nosso Número</Label>
            <Input value={nossoNumero} onChange={e => setNossoNumero(e.target.value)} placeholder="Código do banco" />
          </div>

          <div className="space-y-1">
            <Label>Linha Digitável</Label>
            <Input value={linhaDigit} onChange={e => setLinhaDigit(e.target.value)} placeholder="Linha digitável do boleto" />
          </div>

          <div className="space-y-1">
            <Label>Código de Barras</Label>
            <Input value={codigoBarras} onChange={e => setCodigoBarras(e.target.value)} placeholder="Código de barras" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Taxa Boleto (R$)</Label>
              <Input type="number" step="0.01" value={taxaBoleto} onChange={e => setTaxaBoleto(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label>Observação</Label>
              <Input value={observacao} onChange={e => setObservacao(e.target.value)} placeholder="Opcional" />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => { reset(); onClose(); }}>Cancelar</Button>
          <Button onClick={handleSubmit} disabled={isLoading}>
            {isLoading ? 'Emitindo...' : 'Emitir'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface BaixarDialogProps {
  boleto: BoletoResumo | null;
  onClose: () => void;
  onConfirm: (req: BaixarBoletoRequest) => Promise<void>;
  isLoading: boolean;
}

function BaixarDialog({ boleto, onClose, onConfirm, isLoading }: BaixarDialogProps) {
  const [dtBaixa,       setDtBaixa]       = useState(format(new Date(), 'yyyy-MM-dd'));
  const [valorRecebido, setValorRecebido] = useState(boleto?.valorTotal.toFixed(2) ?? '');
  const [valorDesc,     setValorDesc]     = useState('0');
  const [valorAcres,    setValorAcres]    = useState('0');

  const handleConfirm = async () => {
    await onConfirm({
      dtBaixa,
      valorRecebido: parseFloat(valorRecebido),
      valorDesconto:  parseFloat(valorDesc)  || 0,
      valorAcrescimo: parseFloat(valorAcres) || 0,
    });
    onClose();
  };

  return (
    <Dialog open={!!boleto} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Baixar Boleto #{boleto?.id}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-3 py-2">
          <div className="space-y-1">
            <Label>Data da Baixa *</Label>
            <Input type="date" value={dtBaixa} onChange={e => setDtBaixa(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label>Valor Recebido *</Label>
            <Input type="number" step="0.01" value={valorRecebido} onChange={e => setValorRecebido(e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label>Desconto</Label>
              <Input type="number" step="0.01" value={valorDesc} onChange={e => setValorDesc(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label>Acréscimo</Label>
              <Input type="number" step="0.01" value={valorAcres} onChange={e => setValorAcres(e.target.value)} />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleConfirm} disabled={isLoading}>
            {isLoading ? 'Processando...' : 'Confirmar Baixa'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Página Principal ──────────────────────────────────────────────────────

export default function GestaoBoletos() {
  const [pagina,        setPagina]        = useState(1);
  const [tamanhoPagina, setTamanhoPagina] = useState(20);
  const [filtroStatus,  setFiltroStatus]  = useState<string>('todos');
  // Período pelo VENCIMENTO; abre no mês corrente inteiro (até o último dia), porque
  // boleto vence adiante. Ordem padrão: vencimento crescente — o próximo a vencer
  // (ou o mais atrasado) no topo, como no Contas a Receber.
  const periodo   = usePeriodo('mes', () => setPagina(1), { mesInteiro: true });
  const ordenacao = useOrdenacao<OrdenarPorBoletos>('vencimento', false, () => setPagina(1));

  const isPago = filtroStatus === 'pagos' ? true
               : filtroStatus === 'pendentes' ? false
               : undefined;

  const { boletos, total, totalPaginas, isLoading, isFetching, emitir, isEmitindo, baixar, isBaixando, cancelar } =
    useBoletos({
      isPago,
      ...periodo.intervalo,
      ordenarPor:  ordenacao.ordenarPor,
      decrescente: ordenacao.decrescente,
      pagina,
      tamanhoPagina,
    });

  const firstItem = total === 0 ? 0 : (pagina - 1) * tamanhoPagina + 1;
  const lastItem  = Math.min(pagina * tamanhoPagina, total);
  const handlePageSizeChange = (value: string) => { setTamanhoPagina(Number(value)); setPagina(1); };

  const [emitirOpen,      setEmitirOpen]      = useState(false);
  const [baixarBoleto,    setBaixarBoleto]    = useState<BoletoResumo | null>(null);
  const [cancelarId,      setCancelarId]      = useState<number | null>(null);

  const handleEmitir = async (req: EmitirBoletoRequest) => {
    try {
      await emitir(req);
      toast.success('Boleto emitido com sucesso.');
    } catch {
      toast.error('Erro ao emitir boleto.');
    }
  };

  const handleBaixar = async (req: BaixarBoletoRequest) => {
    if (!baixarBoleto) return;
    try {
      await baixar({ id: baixarBoleto.id, req });
      toast.success('Boleto baixado com sucesso.');
    } catch {
      toast.error('Erro ao baixar boleto.');
    }
  };

  const handleCancelar = async () => {
    if (!cancelarId) return;
    try {
      await cancelar(cancelarId);
      toast.success('Boleto cancelado.');
    } catch {
      toast.error('Erro ao cancelar boleto.');
    } finally {
      setCancelarId(null);
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Gestão de Boletos</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Emissão, baixa e controle de boletos bancários
          </p>
        </div>
        <Button onClick={() => setEmitirOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Emitir Boleto
        </Button>
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap gap-3 items-end">
        <div className="space-y-1">
          <Label className="text-xs">Status</Label>
          <Select value={filtroStatus} onValueChange={v => { setFiltroStatus(v); setPagina(1); }}>
            <SelectTrigger className="w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos</SelectItem>
              <SelectItem value="pendentes">Pendentes</SelectItem>
              <SelectItem value="pagos">Pagos</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Vencimento</Label>
          <div className="flex flex-wrap gap-2">
            <PeriodoFiltro estado={periodo} rotuloData="Vencimento" />
          </div>
        </div>
        <Button variant="outline" size="icon" title="Limpar filtros"
          onClick={() => { setFiltroStatus('todos'); periodo.setPeriodo('mes'); setPagina(1); }}>
          <Search className="h-4 w-4" />
        </Button>
      </div>

      {/* Total */}
      <p className="text-sm text-muted-foreground">{total} boleto(s) encontrado(s)</p>

      {/* Tabela */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <CabecalhoOrdenavel coluna="numero" ordenacao={ordenacao}>ID</CabecalhoOrdenavel>
              <TableHead>Cliente</TableHead>
              <CabecalhoOrdenavel coluna="vencimento" ordenacao={ordenacao} decrescentePadrao={false}>Vencimento</CabecalhoOrdenavel>
              <CabecalhoOrdenavel coluna="valor" ordenacao={ordenacao}>Valor</CabecalhoOrdenavel>
              <TableHead>Nosso Número</TableHead>
              <TableHead>Status</TableHead>
              <CabecalhoOrdenavel coluna="baixa" ordenacao={ordenacao}>Pago em</CabecalhoOrdenavel>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading
              ? Array.from({ length: 8 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 8 }).map((__, j) => (
                      <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>
                    ))}
                  </TableRow>
                ))
              : boletos.length === 0
              ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center text-muted-foreground py-12">
                      Nenhum boleto encontrado.
                    </TableCell>
                  </TableRow>
                )
              : boletos.map(b => {
                  const st = statusBoleto(b);
                  const podeBaixar   = !b.isPago && !b.dtCancelamento && !b.isEstornado;
                  const podeCancelar = !b.isPago && !b.dtCancelamento;
                  return (
                    <TableRow key={b.id}>
                      <TableCell className="font-mono text-xs">{b.id}</TableCell>
                      <TableCell>{b.clienteId}</TableCell>
                      <TableCell>{fmtDate(b.dtVencimento)}</TableCell>
                      <TableCell className="font-medium">{fmt(b.valorTotal)}</TableCell>
                      <TableCell className="font-mono text-xs">{b.nossoNumero ?? '—'}</TableCell>
                      <TableCell>
                        <Badge variant={st.variant}>{st.label}</Badge>
                      </TableCell>
                      <TableCell>{fmtDate(b.dtBaixa)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex gap-1 justify-end">
                          {podeBaixar && (
                            <Button
                              size="sm" variant="outline"
                              onClick={() => setBaixarBoleto(b)}
                              title="Baixar boleto"
                            >
                              <Download className="h-3 w-3 mr-1" />
                              Baixar
                            </Button>
                          )}
                          {podeCancelar && (
                            <Button
                              size="sm" variant="ghost"
                              onClick={() => setCancelarId(b.id)}
                              title="Cancelar boleto"
                              className="text-destructive hover:text-destructive"
                            >
                              <XCircle className="h-3 w-3" />
                            </Button>
                          )}
                          {b.isImpresso && (
                            <Printer className="h-4 w-4 text-muted-foreground" />
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
            }
          </TableBody>
        </Table>
      </div>

      {/* Paginação */}
      <div className="flex flex-wrap items-center justify-between gap-4 pt-2 border-t">
        <p className="text-sm text-muted-foreground">
          {total === 0 ? 'Nenhum registro'
            : `Mostrando ${firstItem}–${lastItem} de ${total} registro${total !== 1 ? 's' : ''}`}
          {isFetching && !isLoading && <span className="ml-2 text-xs">(atualizando...)</span>}
        </p>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground whitespace-nowrap">Itens por página:</span>
            <Select value={String(tamanhoPagina)} onValueChange={handlePageSizeChange}>
              <SelectTrigger className="w-[80px] h-8"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PAGE_SIZES.map((s) => <SelectItem key={s} value={String(s)}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPagina(1)} disabled={pagina === 1}>«</Button>
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPagina((p) => p - 1)} disabled={pagina === 1}><ChevronLeft className="h-4 w-4" /></Button>
            {Array.from({ length: Math.min(5, totalPaginas) }, (_, i) => {
              let page: number;
              if      (totalPaginas <= 5)         page = i + 1;
              else if (pagina <= 3)                page = i + 1;
              else if (pagina >= totalPaginas - 2) page = totalPaginas - 4 + i;
              else                                 page = pagina - 2 + i;
              return (
                <Button key={page} variant={pagina === page ? 'default' : 'outline'}
                  size="icon" className="h-8 w-8 text-xs" onClick={() => setPagina(page)}>{page}</Button>
              );
            })}
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPagina((p) => p + 1)} disabled={pagina >= totalPaginas}><ChevronRight className="h-4 w-4" /></Button>
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPagina(totalPaginas)} disabled={pagina >= totalPaginas}>»</Button>
          </div>
          <span className="text-sm text-muted-foreground whitespace-nowrap">Página {pagina} de {totalPaginas}</span>
        </div>
      </div>

      {/* Dialogs */}
      <EmitirDialog
        open={emitirOpen}
        onClose={() => setEmitirOpen(false)}
        onConfirm={handleEmitir}
        isLoading={isEmitindo}
      />

      <BaixarDialog
        boleto={baixarBoleto}
        onClose={() => setBaixarBoleto(null)}
        onConfirm={handleBaixar}
        isLoading={isBaixando}
      />

      <AlertDialog open={!!cancelarId} onOpenChange={v => { if (!v) setCancelarId(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancelar Boleto</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja cancelar o boleto #{cancelarId}? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Voltar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleCancelar}
            >
              Cancelar Boleto
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
